/**
 * VortexCache - Caches rewritten JS/CSS responses using the Cache API
 * This avoids re-running the expensive WASM rewriter on unchanged content.
 *
 * Cache key = sha256(original url + content-etag/last-modified)
 * so stale content is always invalidated.
 */

const CACHE_NAME = "vortex-rewrite-v1";
const CACHEABLE_TYPES = new Set(["script", "style", "worker", "sharedworker"]);

// Simple fast hash for cache keys (FNV-1a 32-bit)
function fnv1a(str: string): string {
	let hash = 0x811c9dc5;
	for (let i = 0; i < str.length; i++) {
		hash ^= str.charCodeAt(i);
		hash = (hash * 0x01000193) >>> 0;
	}
	return hash.toString(16);
}

function makeCacheKey(
	url: string,
	etag: string | null,
	lastModified: string | null
): string {
	const validator = etag || lastModified || "no-validator";
	return `vortex-cache:${fnv1a(url + validator)}`;
}

export async function getCachedRewrite(
	url: string,
	etag: string | null,
	lastModified: string | null,
	destination: RequestDestination
): Promise<Response | null> {
	if (!CACHEABLE_TYPES.has(destination)) return null;
	try {
		const cache = await caches.open(CACHE_NAME);
		const key = makeCacheKey(url, etag, lastModified);
		const cached = await cache.match(key);
		if (cached) {
			return cached.clone();
		}
	} catch (e) {
		// cache unavailable, skip
	}
	return null;
}

export async function setCachedRewrite(
	url: string,
	etag: string | null,
	lastModified: string | null,
	destination: RequestDestination,
	body: ArrayBuffer | string,
	headers: Record<string, string>
): Promise<void> {
	if (!CACHEABLE_TYPES.has(destination)) return;
	try {
		const cache = await caches.open(CACHE_NAME);
		const key = makeCacheKey(url, etag, lastModified);
		const responseToCache = new Response(body, {
			headers: {
				...headers,
				"x-vortex-cached": "1",
				"x-vortex-url": url,
			},
		});
		await cache.put(key, responseToCache);
	} catch (e) {
		// cache full or unavailable, skip
	}
}

export function isCacheable(destination: RequestDestination): boolean {
	return CACHEABLE_TYPES.has(destination);
}

// Prune old cache entries (run occasionally)
export async function pruneCache(): Promise<void> {
	try {
		const cache = await caches.open(CACHE_NAME);
		const keys = await cache.keys();
		// Keep max 500 entries, delete oldest if over
		if (keys.length > 500) {
			const toDelete = keys.slice(0, keys.length - 500);
			await Promise.all(toDelete.map((k) => cache.delete(k)));
		}
	} catch (e) {
		// ignore
	}
}
